import React from 'react';
import {
    getPortfolioScoreBadgeClass,
    normalizeStoredPortfolioScore,
    PORTFOLIO_SCORE_FIELD,
} from '../utilities/portfolioScoreUtils.js';

const ACTION_CONFIG = [[
    {
        View: {
            title: 'View',
            className: 'btn btn-primary btn-sm',
            icon: 'bi bi-eye me-1',
            label: 'View',
            group: 'secondary',
        },
    },
    {
        Edit: {
            title: 'Edit',
            className: 'btn btn-primary btn-sm',
            icon: 'bi bi-pencil-square me-2',
            label: 'Edit',
            group: 'primary',
        }
    }
], [
    {
        Publish: {
            title: 'Publish/Unpublish',
            className: 'btn btn-primary btn-sm',
            icon: 'bi bi-upload me-2',
            group: 'primary',
        }
    },
    {
        Select: {
            title: 'Select',
            className: 'btn btn-success btn-sm',
            icon: 'bi bi-check2-circle me-1',
            label: 'Select',
            group: 'secondary',
        }
    },
    {
        CopyLink: {
            title: 'Copy Link',
            className: 'btn btn-primary btn-sm',
            icon: 'bi bi-link-45deg me-1',
            label: 'Copy Link',
            group: 'secondary',
        }
    },
],
[
    {
        Rename: {
            title: 'Rename',
            className: 'btn btn-info btn-sm',
            icon: 'bi bi-input-cursor-text me-1',
            label: 'Rename',
            group: 'secondary',
        }
    },
    {
        Copy: {
            title: 'Copy',
            className: 'btn btn-info btn-sm',
            icon: 'bi bi-files me-1',
            label: 'Copy',
            group: 'secondary',
        }
    },
    {
        Delete: {
            title: 'Delete',
            className: 'btn btn-danger btn-sm',
            icon: 'bi bi-trash me-1',
            label: 'Delete',
            group: 'secondary',
        }
    },
]];

const formatUpdatedDate = (updatedAt) => {
    if (!updatedAt) {
        return 'Unknown';
    }

    const dateValue = updatedAt?.toDate?.() || updatedAt;
    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
        return 'Unknown';
    }

    return parsedDate.toLocaleDateString();
};

const PortfolioCard = ({
    portfolio,
    type,
    allowedActions,
    onEdit,
    onTogglePublished,
    onView,
    onCopy,
    onCopyLink,
    onDelete,
    onRename,
    onSelect,
}) => {
    const hasAction = (actionName) => allowedActions.includes(actionName);
    const publishLabel = type === 'Draft' ? 'Publish' : 'Unpublish';
    const latestScore = normalizeStoredPortfolioScore(portfolio?.[PORTFOLIO_SCORE_FIELD]);

    const actionHandlers = {
        Edit: () => onEdit?.(portfolio.id),
        Publish: () => onTogglePublished?.(portfolio.id),
        Rename: () => onRename?.(portfolio.id),
        View: () => onView?.(portfolio.id),
        Copy: () => onCopy?.(portfolio.id),
        CopyLink: () => onCopyLink?.(portfolio.id),
        Delete: () => onDelete?.(portfolio.id),
        Select: () => onSelect?.(portfolio.id),
    };

    const actionGroups = ACTION_CONFIG
        .map((group) =>
            group
                .map((entry) => {
                    const [actionName, action] = Object.entries(entry)[0] || [];

                    if (!actionName || !action || !hasAction(actionName)) {
                        return null;
                    }

                    const label = actionName === 'Publish' ? publishLabel : action.label;

                    return (
                        <button
                            key={actionName}
                            type="button"
                            className={action.className}
                            onClick={actionHandlers[actionName]}
                            title={action.title}
                        >
                            <i className={action.icon}></i>
                            {label}
                        </button>
                    );
                })
                .filter(Boolean)
        )
        .filter((group) => group.length > 0);

    return (
        <div className="card h-100 border-0 shadow-sm hover-lift">
            <div className="card-body d-flex flex-column">
                <div className="mb-3">
                    <h3 className="h5 card-title mb-1 text-truncate">
                        <i className="bi bi-folder me-2" style={{ color: '#667eea' }}></i>
                        {portfolio.title}
                    </h3>
                </div>

                <div className="mb-3 flex-grow-1">
                    <small className="text-muted d-block">
                        <i className="bi bi-calendar2 me-1"></i>
                        Updated {formatUpdatedDate(portfolio.updatedAt)}
                    </small>
                    {latestScore ? (
                        <div className="d-flex align-items-center gap-2 mt-2" title={latestScore.summary || latestScore.ratingLabel}>
                            <span className={`badge ${getPortfolioScoreBadgeClass(latestScore.percentage)}`}>
                                <i className="bi bi-stars me-1" aria-hidden="true"></i>
                                {latestScore.score}/{latestScore.maxScore}
                            </span>
                            <small className="text-muted">AI score</small>
                        </div>
                    ) : null}
                </div>

                {actionGroups.length > 0 ? (
                    <div className="d-flex flex-column gap-2 mt-auto">
                        {actionGroups.map((groupButtons, index) => (
                            <div key={`action-group-${index}`} className="d-flex btn-group-horizontal gap-1">
                                {groupButtons}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default PortfolioCard;
